import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { MobxLitElement } from '@adobe/lit-mobx';
import { styles } from '../styles/shared-styles';
import { settingsIcon } from '../icons/settingsIcon';
import { libraryStore } from '../stores/libraryStore';

@customElement('app-header')
export class AppHeader extends MobxLitElement {
  @state() private isSettingsOpen = false;

  private handleDocumentClick = (event: Event) => {
    const path = event.composedPath();
    if (!path.includes(this)) {
      this.closeSettings();
    }
  };

  private handleDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.closeSettings();
    }
  };

  static styles = [styles, css`
    :host {
      display: block;
      background: var(--bg-color);
      color: var(--text-color, #1f1f1f);
      border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
    }

    #root {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      gap: 16px;
      padding: 0 20px;
      box-sizing: border-box;
    }

    .brand {
      font-size: 20px;
      font-weight: 600;
      letter-spacing: 0.03em;
      color: var(--text-color, #1f1f1f);
    }

    .search-area {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
      max-width: 480px;
    }

    .search-input {
      flex: 1;
      min-width: 0;
      padding: 8px 12px;
      border: 1px solid var(--border-color, rgba(0, 0, 0, 0.12));
      border-radius: 999px;
      background: var(--card-bg-color, rgba(255, 255, 255, 0.92));
      color: inherit;
      outline: none;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }

    .search-input:focus {
      border-color: var(--accent-color, #007acc);
      box-shadow: 0 0 0 3px rgba(0, 122, 204, 0.2);
    }

    .search-button {
      padding: 8px 12px;
      border: none;
      border-radius: 999px;
      background: var(--accent-color, #007acc);
      color: #fff;
      cursor: pointer;
      font-size: 14px;
      transition: background-color 0.2s ease, transform 0.2s ease;
    }

    .search-button:hover {
      background: var(--accent-color-hover, #005a9e);
      transform: translateY(-1px);
    }

    .settings-container {
      position: relative;
      margin-left: auto;
      display: flex;
      align-items: center;
    }

    .settings-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 1px solid transparent;
      background: var(--card-bg-color, rgba(255, 255, 255, 0.92));
      color: var(--text-color, #1f1f1f);
      cursor: pointer;
      transition: background-color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
    }

    .settings-button:focus-visible {
      outline: none;
      border-color: var(--accent-color, #007acc);
      box-shadow: 0 0 0 3px rgba(0, 122, 204, 0.2);
    }

    .settings-button:hover {
      transform: translateY(-1px);
      background: var(--card-bg-color-hover, rgba(255, 255, 255, 0.98));
    }

    .settings-button svg {
      width: 20px;
      height: 20px;
    }

    .settings-popup {
      position: absolute;
      top: calc(100% + 12px);
      right: 0;
      width: min(360px, 90vw);
      padding: 18px;
      border-radius: 16px;
      background: var(--card-bg-color, rgba(255, 255, 255, 0.98));
      box-shadow: 0 16px 32px rgba(0, 0, 0, 0.18);
      border: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
      z-index: 200;
    }

    .settings-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 12px;
    }

    .settings-header h2 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: var(--text-color, #1f1f1f);
    }

    .close-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: none;
      background: transparent;
      color: var(--text-color-secondary, #666);
      cursor: pointer;
      transition: background-color 0.2s ease;
      font-size: 16px;
      line-height: 1;
    }

    .close-button:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    .status-line {
      margin: 4px 0 12px;
      font-size: 12px;
      color: var(--accent-color, #007acc);
      font-style: italic;
    }

    .metadata {
      display: grid;
      gap: 8px;
      margin-bottom: 16px;
      font-size: 12px;
      color: var(--text-color-secondary, #666);
      line-height: 1.4;
      word-break: break-word;
    }

    .metadata strong {
      color: var(--text-color, #1f1f1f);
      font-weight: 600;
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .settings-action {
      padding: 10px 14px;
      border-radius: 8px;
      border: none;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease, transform 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .settings-action.primary {
      background: var(--accent-color, #007acc);
      color: #fff;
    }

    .settings-action.primary:hover:not(:disabled) {
      background: var(--accent-color-hover, #005a9e);
    }

    .settings-action.secondary {
      background: rgba(0, 0, 0, 0.04);
      color: var(--text-color, #1f1f1f);
    }

    .settings-action.secondary:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.08);
    }

    .settings-action.danger {
      background: rgba(220, 53, 69, 0.12);
      color: #b02a37;
    }

    .settings-action.danger:hover:not(:disabled) {
      background: rgba(220, 53, 69, 0.18);
    }

    .settings-action:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    .hint {
      margin: 0;
      font-size: 11px;
      color: var(--text-color-tertiary, #8a8a8a);
    }

    @media(prefers-color-scheme: dark) {
      .settings-action.secondary {
        background: rgba(255, 255, 255, 0.08);
        color: var(--text-color, #f3f3f3);
      }

      .settings-action.secondary:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.12);
      }

      .settings-action.danger {
        background: rgba(220, 53, 69, 0.24);
        color: #ffb3bd;
      }

      .settings-action.danger:hover:not(:disabled) {
        background: rgba(220, 53, 69, 0.32);
      }

      .settings-button {
        background: rgba(255, 255, 255, 0.1);
        color: var(--text-color, #f3f3f3);
      }

      .settings-button:hover {
        background: rgba(255, 255, 255, 0.16);
      }

      .settings-popup {
        background: rgba(32, 32, 32, 0.98);
        border-color: rgba(255, 255, 255, 0.06);
        box-shadow: 0 18px 36px rgba(0, 0, 0, 0.5);
      }

      .close-button:hover {
        background: rgba(255, 255, 255, 0.08);
      }
    }
  `];

  render() {
    const { rootDirectory, isScanning, isLoading, totalFilesFound, lastSaveDate } = libraryStore;
    const hasLibrary = totalFilesFound > 0;

    return html`
      <div id="root">
        <div class="brand">Playa</div>
        <div class="search-area">
          <input
            class="search-input"
            type="search"
            placeholder="Search"
            aria-label="Search library"
          />
          <button class="search-button" type="button">Search</button>
        </div>
        <div class="settings-container">
          <button
            class="settings-button"
            type="button"
            aria-label="Open music library settings"
            aria-haspopup="dialog"
            aria-expanded=${this.isSettingsOpen ? 'true' : 'false'}
            @click=${this.handleSettingsToggle}
          >
            ${settingsIcon}
          </button>
          ${this.isSettingsOpen ? html`
            <div
              class="settings-popup"
              role="dialog"
              aria-label="Music library settings"
              @click=${this.handlePopupClick}
            >
              <div class="settings-header">
                <h2>Music Library</h2>
                <button
                  class="close-button"
                  type="button"
                  aria-label="Close settings"
                  @click=${this.handleCloseClick}
                >
                  ×
                </button>
              </div>
              ${isLoading ? html`<p class="status-line">Loading saved library…</p>` : nothing}
              ${isScanning ? html`<p class="status-line">Scanning for music files…</p>` : nothing}
              <div class="metadata">
                <div><strong>Folder:</strong> ${rootDirectory ?? 'No folder selected'}</div>
                <div><strong>Tracks Indexed:</strong> ${totalFilesFound}</div>
                ${lastSaveDate ? html`<div><strong>Last saved:</strong> ${new Date(lastSaveDate).toLocaleString()}</div>` : nothing}
              </div>
              <div class="actions">
                <button
                  class="settings-action primary"
                  type="button"
                  @click=${this.handleSelectDirectory}
                  ?disabled=${isScanning || isLoading}
                >
                  Import Music
                </button>
                ${hasLibrary ? html`
                  <button
                    class="settings-action secondary"
                    type="button"
                    @click=${this.handleRescanLibrary}
                    ?disabled=${isScanning || isLoading}
                  >
                    Rescan Library
                  </button>
                  <button
                    class="settings-action danger"
                    type="button"
                    @click=${this.handleClearLibrary}
                    ?disabled=${isScanning || isLoading}
                  >
                    Clear Library
                  </button>
                ` : html`<p class="hint">Import a folder to enable rescan and clear actions.</p>`}
              </div>
            </div>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private handleSettingsToggle(event: Event) {
    event.stopPropagation();
    if (this.isSettingsOpen) {
      this.closeSettings();
    } else {
      this.openSettings();
    }
  }

  private handleCloseClick(event: Event) {
    event.stopPropagation();
    this.closeSettings();
  }

  private handlePopupClick(event: Event) {
    event.stopPropagation();
  }

  private openSettings() {
    this.isSettingsOpen = true;
    this.addGlobalListeners();
  }

  private closeSettings() {
    if (this.isSettingsOpen) {
      this.isSettingsOpen = false;
    }
    this.removeGlobalListeners();
  }

  private addGlobalListeners() {
    window.addEventListener('click', this.handleDocumentClick, true);
    window.addEventListener('keydown', this.handleDocumentKeydown, true);
  }

  private removeGlobalListeners() {
    window.removeEventListener('click', this.handleDocumentClick, true);
    window.removeEventListener('keydown', this.handleDocumentKeydown, true);
  }

  private async handleSelectDirectory() {
    try {
      await libraryStore.selectMusicDirectory();
    } catch (error) {
      console.error('Failed to select directory:', error);
      alert('Failed to select directory. Make sure your browser supports the File System API.');
    }
  }

  private handleClearLibrary() {
    libraryStore.clearLibrary();
  }

  private async handleRescanLibrary() {
    try {
      await libraryStore.rescanLibrary();
    } catch (error) {
      console.error('Failed to rescan library:', error);
      alert('Failed to rescan library. Make sure the directory is still accessible.');
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.closeSettings();
  }
}
